import { Pager, PagerArrow, PagerItem } from '@flixbus/honeycomb-react';
import { memo, useMemo } from 'react';

import { CreatePagerItemProps } from './data-table-pager.types';
import React from 'react';
import { Updater } from '@tanstack/react-table';
import createPagerItems from './data-table-pager.utils';
import styles from './data-table-pager.module.scss';

interface Props {
    id: string;
    pageIndex: number;
    pageCount: number;
    setPageIndex: (pageIndex: Updater<number>) => void;
}

const DataTablePager = ({ id, pageIndex, pageCount, setPageIndex }: Props) => {
    const isPagerHidden = pageCount < 2;

    const pagerItemElements = useMemo(() => {
        if (isPagerHidden) {
            return [];
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const items: any = createPagerItems({
            page: pageIndex + 1,
            pageCount,
        });

        return items.map((el: CreatePagerItemProps, i: number) => {
            const { buttonType, nextPage, active, ...restProps } = el;
            const key = `pager-item-${i}`;

            if (buttonType === 'ellipsis') {
                return (
                    <PagerItem key={key} Elem="span" data-testid={`${id}-${buttonType}`} active={active}>
                        ...
                    </PagerItem>
                );
            }

            const handleButtonClick = () => {
                setPageIndex(nextPage - 1);
            };

            if (buttonType === 'previous') {
                return (
                    <PagerArrow
                        {...restProps}
                        key={key}
                        aria-label="Previous Page"
                        side="prev"
                        onClick={handleButtonClick}
                        data-testid={`${id}-${buttonType}`}
                    />
                );
            }

            if (buttonType === 'next') {
                return (
                    <PagerArrow
                        {...restProps}
                        key={key}
                        aria-label="Next Page"
                        side="next"
                        onClick={handleButtonClick}
                        data-testid={`${id}-${buttonType}`}
                    />
                );
            }

            return (
                <PagerItem
                    {...restProps}
                    key={key}
                    onClick={handleButtonClick}
                    data-testid={`${id}-page-${nextPage}`}
                    active={active}
                >
                    {nextPage.toString()}
                </PagerItem>
            );
        });
    }, [id, isPagerHidden, pageCount, pageIndex, setPageIndex]);

    if (isPagerHidden) {
        return null;
    }

    return (
        <div data-testid={id} className={styles.container}>
            <Pager Elem="button" aria-label="Pagination">
                {pagerItemElements}
            </Pager>
        </div>
    );
};

export default memo(DataTablePager);
